import { useEffect, useState } from 'react';
import { usePage } from '../../context/PageContext';
import { v4 as uuidv4 } from 'uuid';
import AddEditTransactionHeader from '../dashboard/addTransaction/addEditTransactionHeader';
import { useParams } from 'react-router-dom';
import { LOCAL_STORAGE_NAMES, ROUTES } from '../../constants/constants';
import { useTranslation } from 'react-i18next';
import { CalendarOutline, Slash } from '../icons';
import { showToast } from '../utils/toast';
import { useUser } from '../../context/UserContext';
import { Form, FormItem, InputNumber } from '../Ant';
import TextAreaField from '../textAreaField/textAreaField';
import { Flex } from 'antd';
import dayjs from 'dayjs';

import { useTimer } from '../../context/TimerContext';
import { ITimer } from '../../interface/timer';
import { formatTime } from '../../constants/utilityFunctions';
import CustomDatePicker from '../Ant/CustomDatePicker/CustomDatePicker';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { useAnalytics } from '../../hooks/useAnalytics';
import { AnalyticsFeatureOperation, AnalyticsPage } from '../../interface/analytics';
import CustomModal from '../informationPopup/customModal';
import { useTimerInitialization } from '../../hooks/useTimerInitialization';
import { pauseRunningTimer } from '../../hooks/useTimerActions';
import { useTransactionParameters } from '../../context/TransactionParameterContext';
import { FormCard } from '../../assets/styles/components/formCard.styled';
import { isTimerFormChanged } from './isTimerFormChanged';
import TimerMatterSelect from './timerMatterSelect';
import TimerSkillSelect from './timerSkillSelect';
import TimerTransactionTypeSelect from './timerTransactionTypeSelect';
import PinnedSaveButton from '../pinnedSaveButton/pinnedSaveButton';

const AddTimer = () => {
  const { setTitle } = usePage();
  const { user } = useUser();
  const { push, goBack } = useNavigationStack();
  const { t } = useTranslation();
  const { timerId } = useParams();
  const { timer, updateTimer, initialTimer } = useTimer();
  const { parameters } = useTransactionParameters();
  const currentOrganisation = localStorage.getItem(LOCAL_STORAGE_NAMES.currentOrganisation);
  const { organizationId } = currentOrganisation ? JSON.parse(currentOrganisation) : {};
  const { trackPage, trackBusinessEvent } = useAnalytics();
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [onDiscardConfirm, setOnDiscardConfirm] = useState<(() => void) | null>(null);

  const fullTime = formatTime(timer?.duration ?? 0); // e.g. "02:15:30"
  const [hours, minutes] = fullTime.split(':');
  const hoursAndMinutes = `${hours}:${minutes}`;
  useTimerInitialization(timer, updateTimer);
  useEffect(() => {
    trackPage(AnalyticsPage.TIMER_ADD_EDIT);
  }, [trackPage]);

  const handleBack = () => {
    updateTimer(null);
    goBack();
  };
  const onBack = () => {
    promptDiscardIfFormChanged(() => {
      handleBack();
    });
  };
  const handleDiscardConfirm = () => {
    onDiscardConfirm?.();
    setShowDiscardDialog(false);
    setOnDiscardConfirm(null);
  };

  const handleDiscardCancel = () => {
    setShowDiscardDialog(false);
    setOnDiscardConfirm(null);
  };
  const handleSelectMatter = () => {
    push(ROUTES.MATTER_SELECTION, {
      isFromTimer: true,
      returnFocusTo: 'matter',
    });
  };
  const handleSkills = () => {
    push(ROUTES.ADD_SKILLS, {
      isFromTimer: true,
      returnFocusTo: 'skill',
    });
  };
  const handleTransactionType = () => {
    push(ROUTES.TRANSACTION_TYPE_SELECTION, {
      isFromTimer: true,
      onlyHourBasedTransactionTypes: true,
      returnFocusTo: 'transactionType',
    });
  };
  const validateFields = () => {
    if (!timer?.subject) {
      return true;
    }

    return false;
  };

  const handleTimerSave = () => {
    if (validateFields()) {
      showToast('info', t('common:fillOutAllRequiredFields'));
      return;
    }
    const existingTimers = localStorage.getItem(LOCAL_STORAGE_NAMES.timer);
    const timers: ITimer[] = existingTimers ? JSON.parse(existingTimers) : [];

    let updatedTimers;

    if (timer?.id) {
      updatedTimers = timers.map((t) =>
        t.id === timer.id
          ? {
              ...t,
              ...timer,
              userId: user?.id,
              timerDate: timer?.timerDate ?? dayjs(),
              organizationId,
            }
          : t,
      );
      trackBusinessEvent(AnalyticsFeatureOperation.TIMER_UPDATED);
    } else {
      const newTimer = {
        ...timer,
        id: uuidv4(),
        userId: user?.id,
        duration: 0,
        timerDate: timer?.timerDate ?? dayjs(),
        organizationId,
      } as ITimer;
      updatedTimers = [...timers, newTimer];
      trackBusinessEvent(AnalyticsFeatureOperation.TIMER_CREATED);
    }

    localStorage.setItem(LOCAL_STORAGE_NAMES.timer, JSON.stringify(updatedTimers));
    handleBack();
  };

  useEffect(() => {
    setTitle(
      <AddEditTransactionHeader
        handleBack={onBack}
        showCross
        validateOnFormChanged={promptDiscardIfFormChanged}
        title={!timer?.id ? t('common:timers.addTimer') : (timer?.subject ?? '')}
        subTitle={!timer?.id ? '' : t('common:timers.editTimer')}
      />,
    );
  }, [timer, timerId, location?.pathname]);

  useEffect(() => {
    if (timerId && !timer?.id) {
      const timer = JSON.parse(localStorage.getItem(LOCAL_STORAGE_NAMES.timer) ?? '[]').find(
        (t: ITimer) =>
          t.id === timerId && t.userId === user?.id && t.organizationId === organizationId,
      );
      if (timer) {
        updateTimer(pauseRunningTimer(timer), true);
      }
    }
  }, [timerId]);

  // Function to prompt discard modal if form changed, or proceed directly
  const promptDiscardIfFormChanged = (onProceed: () => void) => {
    const isFormChanged = isTimerFormChanged(timer, initialTimer, !!timerId);
    setShowDiscardDialog(isFormChanged);
    setOnDiscardConfirm(() => onProceed);
    if (!isFormChanged) {
      onProceed?.();
    }
  };
  return (
    <div className="h-full w-full pb-gutter add-transaction-form">
      <FormCard>
        <Form layout="vertical" className="w-full d-flex fd-column gap-gutter">
          <FormItem
            name="subject"
            label={t('common:subject')}
            required
            rules={[{ required: true, message: t('common:subjectRequired') }]}
          >
            <div className="relative">
              <TextAreaField
                data-testid="timer-subject-input"
                maxLength={1000}
                value={timer?.subject ?? ''}
                placeholder={t('common:addSubject')}
                onChange={(e) => {
                  updateTimer({
                    ...timer,
                    subject: e?.target?.value ?? '',
                    isSubjectManuallyAdded: true,
                  } as ITimer);
                }}
              />
            </div>
          </FormItem>
          <FormItem label={t('matter:matter')} name="matter">
            <div className="relative">
              <TimerMatterSelect
                onOpenPicker={handleSelectMatter}
                onClear={() =>
                  updateTimer({
                    ...timer,
                    matter: undefined,
                    matterId: undefined,
                  } as unknown as ITimer)
                }
              />
            </div>
          </FormItem>

          <Flex className="w-full gap-gutter">
            <FormItem className="w-full" label={t('common:timers.transactionDate')}>
              <CustomDatePicker
                data-testid="timer-transaction-date-picker"
                className="w-full"
                value={timer?.timerDate ? dayjs(timer?.timerDate) : dayjs()}
                onChange={(date) => {
                  const value = date ? date.toISOString() : undefined;
                  updateTimer({
                    ...timer,
                    timerDate: value,
                  } as ITimer);
                }}
                allowClear={false}
                suffixIcon={<CalendarOutline className="calendar-icon" />}
                placement="bottomLeft"
                showWeek
                superNextIcon={null}
                superPrevIcon={null}
              />
            </FormItem>
            <FormItem label={t('common:hours')} className="w-full disabled">
              <InputNumber
                data-testid="timer-hours-input"
                className="w-full"
                placeholder="00:00"
                stringMode
                controls={false}
                disabled
                value={hoursAndMinutes}
              />
            </FormItem>
          </Flex>

          {parameters?.transactionTypeInUse && (
            <FormItem
              name="transactionType"
              label={t('transaction:transactionType.transactionType')}
            >
              <div className="relative">
                <TimerTransactionTypeSelect
                  key={timer?.matter?.id}
                  onOpenPicker={handleTransactionType}
                  onClear={() =>
                    updateTimer({
                      ...timer,
                      transactionType: null,
                      transactionTypeId: null,
                    } as unknown as ITimer)
                  }
                />
              </div>
            </FormItem>
          )}

          <FormItem label={t('transaction:requiredSkill')} name="skill">
            <div className="relative">
              <TimerSkillSelect
                onOpenPicker={handleSkills}
                onClear={() =>
                  updateTimer({
                    ...timer,
                    skill: null,
                    skillId: null,
                  } as unknown as ITimer)
                }
              />
            </div>
          </FormItem>

          <PinnedSaveButton
            data-testid="timer-save-button"
            disabled={validateFields()}
            onClick={handleTimerSave}
          >
            {timerId ? t('common:saveChanges') : t('common:timers.addTimer')}
          </PinnedSaveButton>
        </Form>
      </FormCard>
      <CustomModal
        visible={showDiscardDialog}
        onConfirm={handleDiscardConfirm}
        icon={<Slash />}
        title={t('common:discardChangesText')}
        subdescription={t('common:anyChangeMadeWillBeLostAndCannotBeReverted')}
        confirmText={t('common:discard')}
        cancelText={t('common:cancel')}
        onClose={handleDiscardCancel}
      />
    </div>
  );
};

export default AddTimer;
